import {
  AppBar, Box, Button, Container, IconButton, Stack, Toolbar, Typography, Tooltip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import InsightsIcon from '@mui/icons-material/Insights';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import { Link as RouterLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';

/**
 * Shell rendered around every authenticated route via <Outlet />. Owns the
 * AppBar, top-level nav, and sign-out. Menu items are gated on **effective
 * rights** (role ceiling ∩ group scoping) — same key the server's
 * .RequireRight policy on the matching endpoint checks — so the nav only
 * shows links the user can actually open.
 */
export function MainLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const hasRight = useAuthStore((s) => s.hasRight);
  const originalSession = useAuthStore((s) => s.originalSession);
  const endImpersonation = useAuthStore((s) => s.endImpersonation);
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate('/', { replace: true });   // back to the public holding page
  };

  const onEndImpersonation = () => {
    endImpersonation();
    navigate('/home', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {originalSession && (
        // Impersonation banner — sits above the AppBar so it can't be confused
        // with a normal page header. Warning palette so it reads as "you are
        // not your normal self right now".
        <Box sx={{
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          px: 2, py: 0.5,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          <SupervisorAccountIcon fontSize="small" />
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            Viewing as <strong>{user?.username}</strong> — signed in as <strong>{originalSession.user.username}</strong>.
          </Typography>
          <Button size="small" variant="outlined" color="inherit" onClick={onEndImpersonation}>
            Return to {originalSession.user.username}
          </Button>
        </Box>
      )}
      <AppBar position="static" color="default" elevation={0}
              sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar variant="dense">
          <Typography variant="h6" component="div" sx={{ fontWeight: 700, mr: 4 }}>
            Template.NET
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
            <Button component={RouterLink} to="/home" startIcon={<HomeIcon />} color="inherit" size="small">
              Home
            </Button>
            {hasRight('users.read.list') && (
              <Button component={RouterLink} to="/users" startIcon={<GroupIcon />} color="inherit" size="small">
                Users
              </Button>
            )}
            {hasRight('groups.read.list') && (
              <Button component={RouterLink} to="/groups" startIcon={<GroupsIcon />} color="inherit" size="small">
                Groups
              </Button>
            )}
            {hasRight('roles.read.list') && (
              <Button component={RouterLink} to="/roles" startIcon={<AdminPanelSettingsIcon />} color="inherit" size="small">
                Roles
              </Button>
            )}
            {hasRight('rights.read.list') && (
              <Button component={RouterLink} to="/rights" startIcon={<VpnKeyIcon />} color="inherit" size="small">
                Rights
              </Button>
            )}
            {hasRight('logs.read.query') && (
              <Button component={RouterLink} to="/logs" startIcon={<InsightsIcon />} color="inherit" size="small">
                Logs
              </Button>
            )}
            {hasRight('strava.read.status') && (
              <Button component={RouterLink} to="/strava" startIcon={<DirectionsRunIcon />} color="inherit" size="small">
                Strava
              </Button>
            )}
          </Stack>
          {user && (
            <>
              <Tooltip title="Change password">
                <Button
                  component={RouterLink}
                  to="/change-password"
                  size="small"
                  color="inherit"
                  sx={{ mr: 1, textTransform: 'none', color: 'text.secondary' }}
                >
                  {user.username}
                </Button>
              </Tooltip>
              <Tooltip title="Sign out">
                <IconButton size="small" onClick={onLogout}><LogoutIcon fontSize="small" /></IconButton>
              </Tooltip>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container component="main" maxWidth="xl" sx={{ flexGrow: 1, py: 3 }}>
        <Outlet />
      </Container>
    </Box>
  );
}
